import { ExternalLink, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Offer } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import { InfoHint } from '@ooh/ui/components/info-hint';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { PublicLinkControls } from '../workspace/public-link-controls';
import { PublishRequiredHint } from '../components/publish-required-hint';
import { useOfferHasPublishedVersion } from '../api';
import { derivePublishGateState } from '../lib/publish-gate';

const DIALOG_MAX_WIDTH = 'max-w-lg';

interface ClientShareDialogProps {
  isOpen: boolean;
  offer: Offer;
  offerId: string;
  offerTitle: string;
  /** canEdit && hasPublicOfferLinksFlag — gates the public-link section. */
  canManagePublicLink: boolean;
  /** Absolute or app-relative href to the client preview route. */
  clientPreviewHref: string;
  onClose: () => void;
}

/**
 * Bundles the two ways an offer reaches a client into one coherent surface:
 * the public share link (generate / copy / regenerate / revoke) and the
 * "client view" preview. Opened from the offer header.
 */
export function ClientShareDialog({
  isOpen,
  offer,
  offerId,
  offerTitle,
  canManagePublicLink,
  clientPreviewHref,
  onClose,
}: ClientShareDialogProps) {
  const { t } = useTranslation('offers');

  if (!isOpen) {
    return null;
  }

  return (
    <ModalDialog
      testId="client-share-dialog"
      maxWidth={DIALOG_MAX_WIDTH}
      onClose={onClose}
      closeLabel={t('workspace.header.clientShare.close')}
      header={
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Link2 className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold leading-tight">
              {t('workspace.header.clientShare.title')}
            </h2>
            <p className="truncate text-xs text-muted-foreground">{offerTitle}</p>
          </div>
        </div>
      }
    >
      <ClientShareDialogBody
        canManagePublicLink={canManagePublicLink}
        clientPreviewHref={clientPreviewHref}
        offer={offer}
        offerId={offerId}
      />
    </ModalDialog>
  );
}

interface ClientShareDialogBodyProps {
  canManagePublicLink: boolean;
  clientPreviewHref: string;
  offer: Offer;
  offerId: string;
}

/**
 * Split out from ClientShareDialog so useOfferHasPublishedVersion (and the router/query
 * context it needs) only mounts while the dialog is actually open — mirrors how
 * PublicLinkControls itself is only mounted when canManagePublicLink is true.
 */
function ClientShareDialogBody({
  canManagePublicLink,
  clientPreviewHref,
  offer,
  offerId,
}: ClientShareDialogBodyProps) {
  const { t } = useTranslation('offers');
  const { hasPublishedVersion, isLoading, isUnknownPublishStatus } =
    useOfferHasPublishedVersion(offerId);
  const { disabled: previewDisabled, showHint: showPreviewPublishHint } = derivePublishGateState({
    hasPublishedVersion,
    isLoading,
    isUnknownPublishStatus,
  });

  return (
    <div className="space-y-5">
      {canManagePublicLink ? (
        <section>
          <div className="mb-3 flex items-center gap-1">
            <h3 className="text-xs font-semibold text-foreground">
              {t('workspace.header.clientShare.linkSection')}
            </h3>
            <InfoHint label={t('workspace.header.clientShare.infoLabel')}>
              {t('workspace.header.clientShare.linkInfo')}
            </InfoHint>
          </div>
          <PublicLinkControls
            canManage={canManagePublicLink}
            hasPublishedVersion={hasPublishedVersion}
            isCheckingPublishStatus={isLoading}
            isUnknownPublishStatus={isUnknownPublishStatus}
            offer={offer}
            offerId={offerId}
          />
        </section>
      ) : null}

      <section className={canManagePublicLink ? 'border-t border-border/60 pt-4' : undefined}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            <h3 className="text-xs font-semibold text-foreground">
              {t('workspace.header.clientShare.previewSection')}
            </h3>
            <InfoHint label={t('workspace.header.clientShare.infoLabel')}>
              {t('workspace.header.clientShare.previewInfo')}
            </InfoHint>
          </div>
          {previewDisabled ? (
            <Button
              className="shrink-0 gap-1.5"
              data-testid="client-share-open-preview"
              disabled
              size="sm"
              type="button"
              variant="outline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t('workspace.header.clientShare.openPreview')}
            </Button>
          ) : (
            <a
              data-testid="client-share-open-preview"
              href={clientPreviewHref}
              rel="noreferrer"
              target="_blank"
            >
              <Button className="shrink-0 gap-1.5" size="sm" type="button" variant="outline">
                <ExternalLink className="h-3.5 w-3.5" />
                {t('workspace.header.clientShare.openPreview')}
              </Button>
            </a>
          )}
        </div>
        {showPreviewPublishHint ? (
          <PublishRequiredHint
            className="mt-2"
            message={t('workspace.header.clientShare.requiresPreviewPublish')}
            testId="client-share-preview-requires-publish-hint"
          />
        ) : null}
      </section>
    </div>
  );
}
